import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    CatalogFeaturedLotsInfo,
    FeaturedLotsRequestError,
    SaveFeaturedLotsByCatalogIdParams,
} from '@/redux/modules/featuredLots/featuredLots.types';

const getLotNumbersFromFeaturedLotsErrorMessage = (message: string, leadingMessageRegex: RegExp) => {
    const noLeadErrorMessage = message.replace(leadingMessageRegex, '');
    const noSurroundingCharacters = noLeadErrorMessage.replace(/[[\]:]/g, '').trim();
    return noSurroundingCharacters.replace(/ /g, ', ');
};

const STANDARD_LOT_COUNT = 5;
const PLUS_LOT_COUNT = 10;
const PREMIUM_LOT_COUNT = 20;

export const getFeaturedLotsRequestErrorMessage = (
    error: Error | string,
    rejectWithValue: Parameters<
        Parameters<
            typeof createAsyncThunk<
                CatalogFeaturedLotsInfo | number[],
                number | SaveFeaturedLotsByCatalogIdParams,
                AsyncThunkConfigWithRejectValue
            >
        >[1]
    >[1]['rejectWithValue']
) => {
    let message: string = '';
    if (error instanceof Error) {
        message = error.message.toLowerCase();
    } else if (typeof error === 'string') {
        message = error.toLowerCase();
    }
    message = message.replace('invalid parameters - ', '');
    if (Boolean(message)) {
        if (message.includes('failed to find catalog')) {
            return rejectWithValue(new Error(FeaturedLotsRequestError.CatalogDoesntExist));
        }
        if (message.includes('invalid catalogid')) {
            return rejectWithValue(new Error(FeaturedLotsRequestError.CatalogDoesntExist));
        }
        if (message.includes('lotids cannot be empty')) {
            return rejectWithValue(new Error(FeaturedLotsRequestError.LotNumbersEmpty));
        }
        if (message.includes('duplicate lot number found')) {
            const lotNumbers = getLotNumbersFromFeaturedLotsErrorMessage(message, /duplicate lot number found/i);
            return rejectWithValue(new Error(`${FeaturedLotsRequestError.DuplicateLots} [${lotNumbers}]`));
        }
        if (message.includes('invalid featured lot count')) {
            const hasTenCount = message.includes(`${PLUS_LOT_COUNT}`);
            const counts = [STANDARD_LOT_COUNT];
            if (hasTenCount) {
                counts.push(PLUS_LOT_COUNT);
            }
            counts.push(PREMIUM_LOT_COUNT);
            return rejectWithValue(new Error(`${FeaturedLotsRequestError.InvalidLotCount} [${counts}]`));
        }
        if (message.includes('auction has already ended')) {
            return rejectWithValue(new Error(FeaturedLotsRequestError.AlreadyEnded));
        }
        if (message.includes('invalid lot numbers')) {
            const lotNumbers = getLotNumbersFromFeaturedLotsErrorMessage(message, /invalid lot numbers/i);
            return rejectWithValue(new Error(`${FeaturedLotsRequestError.LotsDontExist} [${lotNumbers}]`));
        }
        if (message.includes('lots already promoted')) {
            const lotNumbers = getLotNumbersFromFeaturedLotsErrorMessage(message, /lots already promoted/i);
            return rejectWithValue(new Error(`${FeaturedLotsRequestError.LotsAlreadyPromoted} [${lotNumbers}]`));
        }
        if (message.includes('lot validation failed')) {
            const cleaned = message.replace('lot validation failed: ', '');
            const capitalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
            return rejectWithValue(new Error(capitalized));
        }
    }
    return rejectWithValue(new Error(FeaturedLotsRequestError.Other));
};
